<template>
  <q-page style="padding: 24px;">
    <div style="max-width: 960px; margin: 0 auto;" v-if="user">
      <!-- Header -->
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <div>
          <h1
            :style="{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--oa-text-primary)',
              margin: 0,
              lineHeight: 1.3,
            }"
          >
            Hola, {{ user.firstName }}
          </h1>
          <p
            :style="{
              fontSize: '0.75rem',
              color: 'var(--oa-text-muted)',
              margin: '2px 0 0 0',
            }"
          >
            {{ roleLabel }}
          </p>
        </div>
      </div>

      <!-- Feature Cards -->
      <div style="margin-top: 20px;">
        <!-- Continue Learning card (full-width) -->
        <div
          :style="{
            background: 'var(--oa-bg-2)',
            border: '1px solid var(--oa-border)',
            borderRadius: '12px',
            padding: '20px',
            borderLeft: '4px solid var(--oa-gold-dim)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }"
        >
          <div
            :style="{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'color-mix(in srgb, var(--oa-gold-full) 12%, transparent)',
              flexShrink: 0,
            }"
          >
            <q-icon name="menu_book" size="24px" style="color: var(--oa-gold-full);" />
          </div>
          <div>
            <div
              :style="{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--oa-text-primary)',
                marginBottom: '4px',
              }"
            >
              Tu curso te espera
            </div>
            <div style="font-size: 0.875rem; color: var(--oa-text-secondary);">
              Los cursos y lecciones estarán disponibles próximamente.
            </div>
          </div>
        </div>
      </div>

      <!-- Role-based cards -->
      <!-- Admin card -->
      <div
        v-if="user.role === 'admin'"
        :style="{
          background: 'var(--oa-bg-2)',
          border: '1px solid var(--oa-border)',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }"
      >
        <div style="display: flex; align-items: center; gap: 12px;">
          <q-icon name="admin_panel_settings" size="24px" style="color: var(--oa-gold-full);" />
          <div>
            <div style="font-size: 0.875rem; font-weight: 600; color: var(--oa-text-primary);">
              Panel de Administración
            </div>
            <div style="font-size: 0.75rem; color: var(--oa-text-muted);">
              Gestioná cursos, unidades y lecciones.
            </div>
          </div>
        </div>
        <q-btn
          outline
          no-caps
          label="Ir al panel"
          to="/admin"
          :style="{ color: 'var(--oa-gold-full)', borderColor: 'var(--oa-gold-dim)' }"
        />
      </div>

      <!-- Allies strip -->
      <div
        style="margin-top: 24px; padding: 20px; background: var(--oa-bg-2); border: 1px solid var(--oa-border); border-radius: 12px;"
      >
        <div style="display: flex; flex-wrap: wrap; gap: 24px; justify-content: center; align-items: center;">
          <img
            v-for="ally in allies"
            :key="ally.label"
            :src="ally.src"
            :alt="ally.label"
            style="height: 56px; width: auto;"
          />
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div
      v-else
      style="max-width: 960px; margin: 0 auto; padding: 24px;"
    >
      <div style="display: flex; align-items: center; margin-bottom: 20px;">
        <div>
          <q-skeleton type="text" width="180px" height="24px" dark />
          <q-skeleton type="text" width="120px" height="14px" dark style="margin-top: 4px;" />
        </div>
      </div>
      <q-skeleton type="rect" height="80px" dark style="border-radius: 12px; margin-top: 20px;" />
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from 'stores/useAuthStore';
import allySummer from 'src/assets/ally-summer-sup-cup.png';
import allyArgentino from 'src/assets/ally-sup-argentino.png';
import allyYca from 'src/assets/ally-sup-argentino-yca.png';

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const allies: { src: string; label: string }[] = [
  { src: allySummer, label: 'Summer SUP Cup' },
  { src: allyArgentino, label: 'SUP Argentino' },
  { src: allyYca, label: 'SUP Argentino YCA' },
];

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  alumno: 'Alumno',
};

const roleLabel = computed(() => {
  if (!user.value) return '';
  return ROLE_LABELS[user.value.role] ?? user.value.role;
});
</script>
